// Workflow Sessions reducer: manages multiple workflow tabs (upload → process → generate)

// Action Types
export const CREATE_WORKFLOW_SESSION = "CREATE_WORKFLOW_SESSION";
export const DELETE_WORKFLOW_SESSION = "DELETE_WORKFLOW_SESSION";
export const SWITCH_WORKFLOW_SESSION = "SWITCH_WORKFLOW_SESSION";
export const UPDATE_WORKFLOW_SESSION_STATE = "UPDATE_WORKFLOW_SESSION_STATE";
export const UPDATE_WORKFLOW_SESSION_NAME = "UPDATE_WORKFLOW_SESSION_NAME";
export const ADD_TO_PROCESSING_QUEUE = "ADD_TO_PROCESSING_QUEUE";
export const REMOVE_FROM_PROCESSING_QUEUE = "REMOVE_FROM_PROCESSING_QUEUE";
export const SET_CURRENTLY_PROCESSING_SESSION = "SET_CURRENTLY_PROCESSING_SESSION";
export const CLEAR_PROCESSING_QUEUE = "CLEAR_PROCESSING_QUEUE";

// Initial state
const initialWorkflowSessionsState = {
  sessions: {}, // { [sessionId]: { sessionId, name, createdAt, state: { documents, extractedData, stepsCompleted, ... } } }
  activeSessionId: null,
  sessionOrder: [], // Array of sessionIds to maintain order
  nextSessionIndex: 1, // For generating sequential session IDs
  processingQueue: [], // Array of workflow sessionIds waiting to process (local queue)
  currentlyProcessingSessionId: null, // The workflow session ID that is currently processing
};

// Helper to generate unique session ID (index-based)
export const generateWorkflowSessionId = (index) => {
  return `workflow-${index}`;
};

// Extract workflow number from session ID (e.g., "workflow-5" -> 5)
const extractWorkflowNumber = (sessionId) => {
  const match = sessionId?.match(/workflow-(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Get next sequential workflow number, finding first available gap
const getNextWorkflowNumber = (sessionOrder) => {
  if (sessionOrder.length === 0) return 1;
  
  // Extract all existing workflow numbers
  const existingNumbers = sessionOrder.map(extractWorkflowNumber).sort((a, b) => a - b);
  
  // Find first gap or use next number
  for (let i = 1; i <= existingNumbers.length + 1; i++) {
    if (!existingNumbers.includes(i)) {
      return i;
    }
  }
  
  // Fallback: use next sequential number
  return existingNumbers[existingNumbers.length - 1] + 1;
};

// Helper to get initial workflow state
export const getInitialWorkflowState = () => ({
  documents: [],
  hasDocuments: false,
  uploadComplete: false,
  uploadedFiles: [],
  extractedData: null,
  stepsCompleted: false,
  selectedSender: [],
  selectedReceiver: [],
  selectedAgent: [],
  editableData: null,
  validationErrors: [],
  preparedJson: null,
  taricJsonErrors: null,
  ocrInputID: null,
  aiSuggestions: [],
  specifyAmount: [],
  additionalContext: '',
  kind: 'export',
  isProcessing: false,
  selectedTemplate: null,
  modalClosed: false,
  isDirty: false,
  uploadSectionCollapsed: false,
  processingError: null,
  suggestionsLoading: false,
});

// Workflow Sessions reducer
const workflowSessionsReducer = (state = initialWorkflowSessionsState, action) => {
  switch (action.type) {
    case CREATE_WORKFLOW_SESSION: {
      const { sessionId, name, initialState } = action.payload;
      
      // Use provided sessionId (restoring) or generate sequential ID (new workflow)
      let newSessionId;
      let workflowNumber;
      
      if (sessionId) {
        newSessionId = sessionId;
        workflowNumber = extractWorkflowNumber(sessionId);
      } else {
        workflowNumber = getNextWorkflowNumber(state.sessionOrder);
        newSessionId = generateWorkflowSessionId(workflowNumber);
      }
      
      // If session already exists, just switch to it
      if (state.sessions[newSessionId]) {
        return {
          ...state,
          activeSessionId: newSessionId,
        };
      }
      
      const newSession = {
        sessionId: newSessionId,
        name: name || `Workflow ${workflowNumber}`,
        createdAt: Date.now(),
        state: initialState || getInitialWorkflowState(),
      };

      // Always add new workflow at the end of sessionOrder
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [newSessionId]: newSession,
        },
        sessionOrder: [...state.sessionOrder, newSessionId],
        activeSessionId: newSessionId,
        nextSessionIndex: Math.max(workflowNumber + 1, state.nextSessionIndex),
      };
    }

    case DELETE_WORKFLOW_SESSION: {
      const { sessionId } = action.payload;
      if (!sessionId || !state.sessions[sessionId]) {
        return state;
      }

      const newSessions = { ...state.sessions };
      delete newSessions[sessionId];

      // Remove from sessionOrder
      const newSessionOrder = state.sessionOrder.filter(id => id !== sessionId);
      
      // If deleted session was active, switch to first remaining session or create a new one
      let newActiveSessionId = state.activeSessionId;
      if (state.activeSessionId === sessionId) {
        if (newSessionOrder.length > 0) {
          newActiveSessionId = newSessionOrder[0];
        } else {
          // Create a new empty session if no sessions remain
          const emptySessionId = generateWorkflowSessionId(1);
          newSessions[emptySessionId] = {
            sessionId: emptySessionId,
            name: 'Workflow 1',
            createdAt: Date.now(),
            state: getInitialWorkflowState(),
          };
          newSessionOrder.push(emptySessionId);
          newActiveSessionId = emptySessionId;
        }
      }

      return {
        ...state,
        sessions: newSessions,
        sessionOrder: newSessionOrder,
        activeSessionId: newActiveSessionId,
      };
    }

    case SWITCH_WORKFLOW_SESSION: {
      const { sessionId } = action.payload;
      if (!sessionId || !state.sessions[sessionId]) {
        console.warn('SWITCH_WORKFLOW_SESSION: Invalid sessionId', sessionId);
        return state;
      }

      return {
        ...state,
        activeSessionId: sessionId,
      };
    }

    case UPDATE_WORKFLOW_SESSION_STATE: {
      const { sessionId, stateUpdates } = action.payload;
      if (!sessionId || !state.sessions[sessionId]) {
        return state;
      }

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...state.sessions[sessionId],
            state: {
              ...state.sessions[sessionId].state,
              ...stateUpdates,
            },
          },
        },
      };
    }

    case UPDATE_WORKFLOW_SESSION_NAME: {
      const { sessionId, name } = action.payload;
      if (!sessionId || !state.sessions[sessionId] || !name) {
        return state;
      }

      // Prevent unnecessary updates if name is already the same
      if (state.sessions[sessionId].name === name) {
        return state;
      }

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...state.sessions[sessionId],
            name: name,
          },
        },
      };
    }

    case ADD_TO_PROCESSING_QUEUE: {
      const { sessionId } = action.payload;
      if (!sessionId || !state.sessions[sessionId]) {
        return state;
      }

      // Don't add if already in queue or currently processing
      if (state.processingQueue.includes(sessionId) || state.currentlyProcessingSessionId === sessionId) {
        return state;
      }

      return {
        ...state,
        processingQueue: [...state.processingQueue, sessionId],
      };
    }

    case REMOVE_FROM_PROCESSING_QUEUE: {
      const { sessionId } = action.payload;
      return {
        ...state,
        processingQueue: state.processingQueue.filter(id => id !== sessionId),
      };
    }

    case SET_CURRENTLY_PROCESSING_SESSION: {
      const { sessionId } = action.payload;
      // If null, clear processing; otherwise remove from queue and set as currently processing
      if (sessionId === null) {
        return {
          ...state,
          currentlyProcessingSessionId: null,
        };
      }

      if (!state.sessions[sessionId]) {
        return state;
      }

      return {
        ...state,
        currentlyProcessingSessionId: sessionId,
        processingQueue: state.processingQueue.filter(id => id !== sessionId),
      };
    }

    case CLEAR_PROCESSING_QUEUE: {
      return {
        ...state,
        processingQueue: [],
        currentlyProcessingSessionId: null,
      };
    }

    default:
      return state;
  }
};

// Action Creators
export const createWorkflowSession = (sessionId = null, name = null, initialState = null) => ({
  type: CREATE_WORKFLOW_SESSION,
  payload: { sessionId, name, initialState },
});

export const deleteWorkflowSession = (sessionId) => ({
  type: DELETE_WORKFLOW_SESSION,
  payload: { sessionId },
});

export const switchWorkflowSession = (sessionId) => ({
  type: SWITCH_WORKFLOW_SESSION,
  payload: { sessionId },
});

export const updateWorkflowSessionState = (sessionId, stateUpdates) => ({
  type: UPDATE_WORKFLOW_SESSION_STATE,
  payload: { sessionId, stateUpdates },
});

export const updateWorkflowSessionName = (sessionId, name) => ({
  type: UPDATE_WORKFLOW_SESSION_NAME,
  payload: { sessionId, name },
});

export const addToProcessingQueue = (sessionId) => ({
  type: ADD_TO_PROCESSING_QUEUE,
  payload: { sessionId },
});

export const removeFromProcessingQueue = (sessionId) => ({
  type: REMOVE_FROM_PROCESSING_QUEUE,
  payload: { sessionId },
});

export const setCurrentlyProcessingSession = (sessionId) => ({
  type: SET_CURRENTLY_PROCESSING_SESSION,
  payload: { sessionId },
});

export const clearProcessingQueue = () => ({
  type: CLEAR_PROCESSING_QUEUE,
});

export default workflowSessionsReducer;

